/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import customProgress from "../../../../share/components/vue/CustomProgress";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import api from '@/common/api';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';
import util from '../../../../share/util/utils';
import handler from '../../../../share/util/apiResultValidator';
import alert from '../../../../share/alert';
import reprogram from '../../../../share/util/reprogram';

/** 各フェーズ定義 */
const PHASE = {
  START: "start",
  IN_PROGRESS: "inProgress",
  STOP: "stop",
  ERROR: "error"
};

export default {
  name: "requirement130",
  data() {
    return {
      systemId: '',
      phase: PHASE.START,
      isLoading: false,
      readRequestId: '',
      uploadRequestId: '',
      uploadFileName: '',
      totalTargetCount: 0,
      filePath: '',
      progress: 0,
      uploadRetryCount: 0,
      modal_requirement_130: false,
      uploadTime: '',
      uploadSpeed: ''
    };
  },

  components: {
    'custom-Progress': customProgress,
  },


  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      isTestMode: state => state.netAppConnector.isTestMode,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
          return "danger";
        case PHASE.STOP:
          return "success";
        case PHASE.START:
        case PHASE.IN_PROGRESS:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_130 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_130 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result handler.validate で返ってきたバリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 実行時の起動処理 */
    async execute() {
      try {
        this.isLoading = true;

        // API実行
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              this.readRequestId = response.data.user_data.request_id;
              this.totalTargetCount = response.data.user_data.total_target_count;
              // 正常終了：クラウドへのデータ保存
              this.phase = PHASE.IN_PROGRESS;
              await this.upload();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** クラウドへのデータ保存 */
    async upload() {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        // リクエストボディの初期化
        const requestBody = {
          id_token: token.id_token,
          type: 'upload'
        };

        // isTestModeがtrueの場合、is_training_modeを追加
        if (this.isTestMode) {
          requestBody.is_training_mode = true;
        } else {
          // isTestModeがfalseの場合、request_idを追加
          requestBody.request_id = this.readRequestId;
        }

        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED: {
              this.uploadRequestId = response.data.user_data.request_id;
              this.uploadRetryCount = 0;
              // 完了数により処理を分岐
              await this.targetCountJudge(response.data.user_data);
              break;
            }
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** クラウドへのデータ保存(GETのみ) */
    async getUploadResult() {
      // sleep
      await util.sleep(process.env.VUE_APP_WORK_SUPPORT_RETRY_INTERVAL);
      try {
        this.isLoading = true;
        // バックエンドAPI GETのみ
        const response = await individualDiagnose.syncApi(
          this,
          // 呼び出したい GET API を実装する。
          async () => {
            return await api.getCall(
              config.WSP,
              '/netApps/' + this.netAppId + '/systems/' + this.systemId + '/workSupports/'
              + this.workSupportId + '/requests/' + this.uploadRequestId
            );
          },
          // エラー発生のコールバックを実装
          (errorType) => { errorType; this.showUnexpectedError(); },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => { getApiResponse; return ''; },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED: {
              // 完了数により処理を分岐
              await this.targetCountJudge(response.data.user_data);
              break;
            }
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * クラウドへのデータ保存数を判定し処理を分岐
     * @param userData APIレスポンスのuser_data
     */
    async targetCountJudge(userData) {
      const count = userData.completed_target_count;
      this.progress = Math.round((count / this.totalTargetCount) * 100);
      if (0 <= count && count < this.totalTargetCount) {
        if (this.uploadRetryCount > process.env.VUE_APP_WORK_SUPPORT_RETRY_COUNT) {
          this.showUnexpectedError();
        } else {
          await this.getUploadResult();
          this.uploadRetryCount++;
        }
      } else if (count === this.totalTargetCount) {
        await util.sleep(1000); // 進捗率が100%であることを明示してから画面を切り替える
        if (userData.file_path && userData.file_name) {
          this.phase = PHASE.STOP;
          this.filePath = userData.file_path;
          this.uploadFileName = userData.file_name;

          // uploadTime を変換して表示用に設定
          const conversionInfo = reprogram.calculateFileDownloadTime(userData.upload_time);
          if (conversionInfo) {
            let uploadTimeDisplay = '';
            if (conversionInfo.minutes > 0) {
              uploadTimeDisplay = `${conversionInfo.minutes} ${this.$t('message.label_repro_execute_dl_unit_min')} `;
            }
            uploadTimeDisplay = `${uploadTimeDisplay}${conversionInfo.seconds} ${this.$t('message.label_repro_execute_dl_unit_sec')}`;
            this.uploadTime = uploadTimeDisplay; // 変換した値を設定
          } else {
            this.uploadTime = `- ${this.$t('message.label_repro_execute_dl_unit_sec')}`; // 変換できなかった場合
          }


          // uploadSpeed を変換して表示用に設定
          const uploadSpeed = Number(userData.upload_speed);
          const speed = reprogram.toFixedDownloadSpeed(uploadSpeed, 0.0);
          if (speed) {
            this.uploadSpeed = `${speed} ${this.$t('message.label_training_dl_unit_speed')}`; // 変換した値を設定
          } else {
            this.uploadSpeed = `- ${this.$t('message.label_training_dl_unit_speed')}`; // 変換できなかった場合
          }
        } else {
          this.showUnexpectedError();
        }
      } else {
        this.showUnexpectedError();
      }
    },

    /** ダウンロード処理 */
    async download() {
      try {
        this.isLoading = true;

        // ダウンロード元のファイルパス
        const params = {
          file_path: this.filePath
        };
        // ダウンロードURL処理
        const response = await api.getParamCall(config.WSP, '/downloadUrl', params);
        // ダウンロードURL処理のResponseをチェックする
        handler.validate(
          handler.validateTypes.all,
          response, this, null,
          () => {// 処理が成功したときのコールバック処理
            try {
              //URLからファイルをダウンロード
              fetch(response.data.fileUrl)
                .then((response) => response.blob())
                .then((myBlob) => {
                  const objUrl = URL.createObjectURL(myBlob);
                  const link = document.createElement("a");
                  link.href = objUrl;
                  link.download = this.uploadFileName;
                  link.click();
                  link.remove();
                  URL.revokeObjectURL(objUrl);
                });
            } catch {
              this.phase = PHASE.ERROR;
            }
          },
          (response) => {// 処理が失敗したときのコールバック処理
            if (response.causeType === handler.causeTypes.permissionError) {
              // ユーザー権限エラーの場合、エラーダイアログを表示しログアウトする
              alert.showError(this, null, response.message, () => {
                this.logout(this);
              });
            } else {
              this.phase = PHASE.ERROR;
            }
          }, null, false
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** 停止処理 */
    async exit() {
      try {
        this.isLoading = true;

        // API実行
        //バックエンドAPI POST,GET
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // 結果にかかわらず機能終了
        this.closeWizard();

      } finally {
        this.isLoading = false;
      }
    },
  },

  async mounted() {
    this.init();
  }
};
